import React, { useMemo, useState } from 'react';
import Button from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import { useIntl } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import {
	getScorecardHealthCountsFromScores,
	getSummaryText,
	type ScorecardHealthCount,
	type ScorecardScore,
} from '../../common/utils.tsx';
import ScorecardsHealthInfoDetails from './popup-content/index.tsx';

type Props = {
	componentId: string;
	scorecardScores: ScorecardScore[];
	onClick?: (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
};

export default function ScorecardsHealthLozenge(props: Props) {
	const { componentId, scorecardScores, onClick } = props;
	const { formatMessage } = useIntl();

	const [isOpen, setIsOpen] = useState(false);
	const [isLozengeHovered, setIsLozengeHovered] = useState(false);

	const scorecardHealthCount = useMemo(
		() => getScorecardHealthCountsFromScores(scorecardScores),
		[scorecardScores],
	);

	const summary = useMemo(
		() => getSummaryText(scorecardHealthCount, scorecardScores.length, formatMessage),
		[scorecardHealthCount, scorecardScores.length, formatMessage],
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-start"
			messageId="compass-common.ui.scorecards-health-lozenge.popup"
			messageType="transactional"
			content={() => <ScorecardsHealthInfoDetails componentId={componentId} />}
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					appearance="subtle"
					spacing="none"
					onClick={(e, analyticsEvent) => {
						setIsOpen(!isOpen);
						onClick?.(e, analyticsEvent);
					}}
				>
					{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
					<div
						onMouseOver={() => {
							setIsLozengeHovered(true);
						}}
						onFocus={() => {
							setIsLozengeHovered(true);
						}}
						onMouseLeave={() => {
							setIsLozengeHovered(false);
						}}
						onBlur={() => {
							// React uses onFocus and onBlur instead of onFocusIn and onFocusOut.
							setIsLozengeHovered(false);
						}}
					>
						<Lozenge isBold={isLozengeHovered} appearance={getLozengeStyle(scorecardHealthCount)}>
							{summary}
						</Lozenge>
					</div>
				</Button>
			)}
		/>
	);
}

const getLozengeStyle = (scorecardHealthCount: ScorecardHealthCount) => {
	if (
		scorecardHealthCount.passing + scorecardHealthCount.needsAttention <
		scorecardHealthCount.total
	) {
		return 'removed';
	}
	if (scorecardHealthCount.needsAttention > 0) {
		return 'moved';
	}
	return 'success';
};
